import { Engine, Scene, Label, Font, Color, Vector, vec, Keys, TextAlign, CoordPlane, ScreenElement, Actor } from "excalibur";
import { Resources } from "../resources";

/**
 * VictoryScene - Victory Screen
 * 
 * Displayed when the player survives the full 5 minutes.
 * Shows "Congratulations Riley!" with the character image and "Virus Defeated".
 * 
 * Implements responsive layout that adapts to both landscape and portrait orientations.
 */
export class VictoryScene extends Scene {
    private background!: ScreenElement;
    private characterImage!: Actor;
    private congratsLabel!: Label;
    private defeatedLabel!: Label;
    private replayLabel!: Label;
    private time: number = 0;
    private resizeHandler: (() => void) | null = null;

    override onInitialize(engine: Engine): void {
        // Background image
        this.background = new ScreenElement({
            pos: vec(0, 0),
            anchor: Vector.Half,
        });
        const bgSprite = Resources.VictoryBackground.toSprite();
        this.background.graphics.use(bgSprite);
        this.add(this.background);

        // Character image in the middle
        this.characterImage = new Actor({
            pos: vec(0, 0), // Will be set by updateLayout
            anchor: Vector.Half,
        });
        this.characterImage.transform.coordPlane = CoordPlane.Screen;
        const charSprite = Resources.IntroCharacter.toSprite();
        this.characterImage.graphics.use(charSprite);
        this.add(this.characterImage);

        // "Congratulations Riley!" label (two lines) in upper portion
        this.congratsLabel = new Label({
            text: 'Congratulations\nRiley!',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 48,
                bold: true,
                color: Color.fromHex('#fbbf24'), // Gold/yellow
                textAlign: TextAlign.Center,
                shadow: {
                    blur: 8,
                    offset: vec(2, 2),
                    color: Color.Black,
                },
            }),
            anchor: Vector.Half,
        });
        this.congratsLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.congratsLabel);

        // "Virus Defeated" label below character
        this.defeatedLabel = new Label({
            text: 'Virus Defeated',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 36,
                bold: true,
                color: Color.fromHex('#4ade80'), // Green
                textAlign: TextAlign.Center,
                shadow: {
                    blur: 6,
                    offset: vec(2, 2),
                    color: Color.Black,
                },
            }),
            anchor: Vector.Half,
        });
        this.defeatedLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.defeatedLabel);

        // Replay prompt
        this.replayLabel = new Label({
            text: 'Tap to play again',
            pos: vec(0, 0), // Will be set by updateLayout
            font: new Font({
                family: 'Courier New, monospace',
                size: 18,
                color: Color.White,
                textAlign: TextAlign.Center,
                shadow: {
                    blur: 4,
                    offset: vec(1, 1),
                    color: Color.Black,
                },
            }),
            anchor: Vector.Half,
        });
        this.replayLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.replayLabel);

        // Tap to replay
        engine.input.pointers.primary.on('down', () => {
            if (engine.currentSceneName === 'victory') {
                engine.goToScene('title');
            }
        });

        engine.input.keyboard.on('press', (evt) => {
            if (engine.currentSceneName === 'victory' && (evt.key === Keys.Space || evt.key === Keys.Enter)) {
                engine.goToScene('title');
            }
        });

        // Initial layout
        this.updateLayout(engine);
    }

    override onActivate(): void {
        // Stop game music
        Resources.GameMusic.stop();

        // Play game win sound, then start menu music when done
        Resources.GameWin.play().then(() => {
            // Start menu music after win sound completes
            if (this.engine.currentSceneName === 'victory') {
                Resources.MenuMusic.loop = true;
                Resources.MenuMusic.play();
            }
        });

        // Listen for browser resize events
        this.resizeHandler = () => this.updateLayout(this.engine);
        this.engine.screen.events.on('resize', this.resizeHandler);

        // Update layout immediately
        this.updateLayout(this.engine);
    }

    override onDeactivate(): void {
        // Stop menu music when leaving victory screen
        Resources.MenuMusic.stop();

        // Cleanup listener
        if (this.resizeHandler) {
            this.engine.screen.events.off('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
    }

    /**
     * Update element positions based on current screen size
     * Supports both landscape and portrait orientations
     */
    private updateLayout(engine: Engine): void {
        const w = engine.drawWidth;
        const h = engine.drawHeight;
        const centerX = w / 2;
        const centerY = h / 2;
        const isPortrait = h > w;

        // Scale background to cover full screen
        const bgSprite = Resources.VictoryBackground.toSprite();
        const scaleX = w / bgSprite.width;
        const scaleY = h / bgSprite.height;
        const bgScale = Math.max(scaleX, scaleY); // Cover mode
        bgSprite.scale = vec(bgScale, bgScale);
        this.background.graphics.use(bgSprite);
        // Center the background (anchor is Half, so position at screen center)
        this.background.pos = vec(centerX, centerY);

        // Character sprite scaling based on orientation
        const charSprite = Resources.IntroCharacter.toSprite();
        let charScale: number;
        if (isPortrait) {
            // Portrait: scale based on width, smaller
            charScale = Math.min(w * 0.6 / charSprite.width, h * 0.35 / charSprite.height);
        } else {
            // Landscape: scale based on height
            charScale = Math.min(w * 0.4 / charSprite.width, h * 0.5 / charSprite.height);
        }
        charSprite.scale = vec(charScale, charScale);
        this.characterImage.graphics.use(charSprite);
        this.characterImage.pos = vec(centerX, centerY);

        // Font sizes based on orientation
        const congratsFontSize = isPortrait ? Math.min(36, w * 0.08) : Math.min(48, w * 0.05);
        const defeatedFontSize = isPortrait ? Math.min(28, w * 0.06) : Math.min(36, w * 0.04);
        const replayFontSize = isPortrait ? 16 : 18;

        // Update font sizes
        this.congratsLabel.font = new Font({
            family: 'Courier New, monospace',
            size: congratsFontSize,
            bold: true,
            color: Color.fromHex('#fbbf24'),
            textAlign: TextAlign.Center,
            shadow: {
                blur: 8,
                offset: vec(2, 2),
                color: Color.Black,
            },
        });

        this.defeatedLabel.font = new Font({
            family: 'Courier New, monospace',
            size: defeatedFontSize,
            bold: true,
            color: Color.fromHex('#4ade80'),
            textAlign: TextAlign.Center,
            shadow: {
                blur: 6,
                offset: vec(2, 2),
                color: Color.Black,
            },
        });

        this.replayLabel.font = new Font({
            family: 'Courier New, monospace',
            size: replayFontSize,
            color: Color.White,
            textAlign: TextAlign.Center,
            shadow: {
                blur: 4,
                offset: vec(1, 1),
                color: Color.Black,
            },
        });

        // Position text closer to screen edges
        const topMargin = isPortrait ? h * 0.08 : h * 0.1;
        const bottomMargin = isPortrait ? h * 0.15 : h * 0.12;

        // "Congratulations Riley!" near top of screen
        this.congratsLabel.pos = vec(centerX, topMargin + congratsFontSize);

        // "Virus Defeated" near bottom of screen (above replay)
        this.defeatedLabel.pos = vec(centerX, h - bottomMargin - defeatedFontSize);

        // Replay at bottom
        this.replayLabel.pos = vec(centerX, h - 40);
    }

    override onPreUpdate(_engine: Engine, delta: number): void {
        this.time += delta;

        // Pulsing animation for replay text
        const pulse = 0.5 + Math.sin(this.time / 400) * 0.5;
        if (this.replayLabel) {
            this.replayLabel.graphics.opacity = pulse;
        }

        // Gentle bobbing for character
        if (this.characterImage) {
            const bob = Math.sin(this.time / 800) * 5;
            const baseY = this.engine.drawHeight / 2;
            this.characterImage.pos.y = baseY + bob;
        }
    }
}
